import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, filter, finalize, switchMap, take, throwError } from 'rxjs';
import { APP_API_ENDPOINTS } from '@core/constants/app-api.const';
import { APP_PAGES } from '@core/constants/app-pages.const';
import { AppError } from '@core/models/api.model';
import { extractErrorMessage } from '@shared/utils/error.util';
import { AuthService } from '@core/services/auth.service';
import { AuthStore } from '@core/stores/auth.store';

/** Token đánh dấu request đã được retry sau khi làm mới token (chỉ lưu cục bộ, không gửi lên server) */
export const IS_RETRIED_401 = new HttpContextToken<boolean>(() => false);

/**
 * Đổi lỗi refresh token thành AppError để toast hiển thị được.
 * HttpErrorResponse 401/403 bị BaseComponent bỏ toast (xem handleCommonError), nên không đổi thì không thấy thông báo.
 */
const toRefreshAppError = (err: unknown): AppError => {
  if (err instanceof AppError) return err;
  return new AppError({
    status: false,
    mess: extractErrorMessage(err, 'COMMON.API_FAILED'),
    data: null,
  });
};

/**
 * 🚨 Enterprise Error Interceptor (Bắt lỗi toàn cục API)
 * - Tự động phát hiện lỗi Business (status: false).
 * - Tự động phát hiện lỗi HTTP (401, 403, 500...).
 * - Tự động Refresh Token khi gặp lỗi 401 (Unauthorized).
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const authStore = inject(AuthStore);

  /**
   * Xử lý lỗi 401: Refresh Token Queueing Logic
   */
  const handle401Error = (request: HttpRequest<unknown>) => {
    if (authService.isRefreshing) {
      // Đã có request khác đang refresh, ta chỉ cần đợi tín hiệu từ Subject
      return authService.refreshTokenSubject.pipe(
        // ✅ Chờ tín hiệu khác null ban đầu (khi refreshTokenSubject.next(null) được gọi)
        // Nhưng PHẢI cho qua cả giá trị rỗng '' để unlock hàng đợi khi refresh thất bại
        filter((token) => token !== null),
        take(1),
        switchMap((token) => {
          // Token rỗng '' = refresh thất bại → trả lỗi cho request trong hàng đợi
          if (!token) {
            return throwError(() => authService.refreshError ?? new Error('COMMON.API_FAILED'));
          }
          return next(
            request.clone({
              context: request.context.set(IS_RETRIED_401, true),
              headers: request.headers.set('Authorization', `Bearer ${token}`),
            }),
          );
        }),
      );
    }

    // Đây là request đầu tiên phát hiện 401 -> Kích hoạt Refresh Token
    authService.isRefreshing = true;
    authService.refreshError = null;
    authService.refreshTokenSubject.next(null);

    return authService.refreshTokenApi().pipe(
      catchError((err) => {
        const failure = toRefreshAppError(err);
        // Lưu lỗi trước khi phát '' để hàng đợi nhận đúng message từ server
        authService.refreshError = failure;
        // ✅ Gửi chuỗi rỗng '' thay vì null để unlock hàng đợi
        authService.refreshTokenSubject.next('');
        authService.forceLogout(); // Dọn dẹp local mà KHÔNG gọi API logout
        return throwError(() => failure);
      }),
      switchMap((response) => {
        const newToken = response.data;
        if (newToken) {
          authStore.setToken(newToken);
          authService.refreshTokenSubject.next(newToken); // Thông báo cho hàng đợi

          // Chạy lại request bị lỗi ban đầu với Token mới + đánh dấu đã retry để tránh loop
          return next(
            request.clone({
              context: request.context.set(IS_RETRIED_401, true),
              headers: request.headers.set('Authorization', `Bearer ${newToken}`),
            }),
          );
        }
        // Server trả 200 nhưng không có token: dùng mess của response làm message lỗi
        const failure = new AppError(response);
        authService.refreshError = failure;
        authService.refreshTokenSubject.next('');
        authService.forceLogout();
        return throwError(() => failure);
      }),
      finalize(() => (authService.isRefreshing = false)),
    );
  };

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (!(error.error instanceof ErrorEvent)) {
        // Các API Auth không cần trigger Refresh Token để tránh vòng lặp
        const noRefreshEndpoints = [
          APP_API_ENDPOINTS.AUTH.LOGIN,
          APP_API_ENDPOINTS.AUTH.REFRESH_TOKEN,
          APP_API_ENDPOINTS.AUTH.GET_OTP,
          APP_API_ENDPOINTS.AUTH.VERIFY_OTP,
          APP_API_ENDPOINTS.AUTH.FORGOT_PASSWORD,
          APP_API_ENDPOINTS.AUTH.CHANGE_EXPIRED_PASSWORD,
        ];
        const isNoRefreshApi = noRefreshEndpoints.some((endpoint) => req.url.includes(endpoint));

        // Kiểm tra nếu request này đã được retry nhưng vẫn 401 -> Dừng lại để tránh loop
        const isRetried = req.context.get(IS_RETRIED_401);

        switch (error.status) {
          case 401:
            // ✅ Chỉ Refresh nếu không thuộc danh sách bỏ qua và chưa retry lần nào
            if (!isNoRefreshApi && !isRetried) return handle401Error(req);

            // Nếu API Refresh Token lỗi 401 hoặc request đã retry vẫn 401 -> Logout
            if (req.url.includes(APP_API_ENDPOINTS.AUTH.REFRESH_TOKEN) || isRetried) {
              authService.forceLogout();
            }
            // ✅ Không hiển thị toast cho 401 → return sớm
            return throwError(() => error);
          case 403:
            if (!router.url.includes(APP_PAGES.FORBIDDEN.path)) {
              router.navigate(['/' + APP_PAGES.FORBIDDEN.path]).then();
            }
            break;
        }
      }

      // Ném lỗi tiếp để BaseComponent xử lý Toast (single source of truth)
      return throwError(() => error);
    }),
  );
};
